import React, { useState, useEffect, useRef } from "react";
import Header from "../../../Components/Header";
import Sidebar from "../../../Components/Sidebar";
import { Link, useNavigate, useParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { getDocument, updateDocument } from "../../../firebase/dbService";
import { uploadFile, getFileURL } from "../../../firebase/storageService";
import { calendar } from "../../../Components/imagepath";
import TextEditor from "./TextEditor";

const EditInformationCard = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        Title: "",
        Description: "",
        imageUrl: "",
        // German Address Format
        streetAddress: "",
        houseNumber: "",
        postalCode: "",
        city: "",
        state: "",
        country: "Deutschland",
        // Event Details
        eventDate: "",
        eventTime: "",
        contact: "",
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [currentImageUrl, setCurrentImageUrl] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const editorRef = useRef(null);

    useEffect(() => {
        const fetchDocumentData = async () => {
            try {
                const documentSnapshot = await getDocument('informationCard', id);
                if (documentSnapshot.exists()) {
                    const data = documentSnapshot.data();
                    setFormData({
                        Title: data.Title,
                        Description: data.Description,
                        imageUrl: data.imageUrl || "",
                        // German Address Format
                        streetAddress: data.streetAddress || "",
                        houseNumber: data.houseNumber || "",
                        postalCode: data.postalCode || "",
                        city: data.city || "",
                        state: data.state || "",
                        country: data.country || "Deutschland",
                        // Event Details
                        eventDate: data.eventDate || "",
                        eventTime: data.eventTime || "",
                        contact: data.contact || "",
                    });
                    
                    // Load current image if exists
                    if (data.imageUrl) {
                        try {
                            const imageUrl = await getFileURL(data.imageUrl);
                            setCurrentImageUrl(imageUrl);
                        } catch (error) {
                            console.error('Error loading image:', error);
                        }
                    }
                    editorRef.current.setEditorContent(data.Description);
                } else {
                    console.error('Document does not exist');
                }
            } catch (error) {
                console.error('Error fetching document data:', error);
            }
        };

        fetchDocumentData();
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setSelectedFile(null);
        setImagePreview(null);
        setCurrentImageUrl(null);
        setFormData(prev => ({ ...prev, imageUrl: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.Title.trim()) {
            toast.error("Event title is required", { autoClose: 2000 });
            return;
        }

        if (!formData.Description.trim()) {
            toast.error("Event description is required", { autoClose: 2000 });
            return;
        }

        if (!formData.streetAddress.trim()) {
            toast.error("Street address is required", { autoClose: 2000 });
            return;
        }

        if (!formData.houseNumber.trim()) {
            toast.error("House number is required", { autoClose: 2000 });
            return;
        }

        if (!formData.postalCode.trim()) {
            toast.error("Postal code is required", { autoClose: 2000 });
            return;
        }

        if (!formData.city.trim()) {
            toast.error("City is required", { autoClose: 2000 });
            return;
        }

        if (!formData.eventDate) {
            toast.error("Event date is required", { autoClose: 2000 });
            return;
        }

        if (!formData.eventTime) {
            toast.error("Event time is required", { autoClose: 2000 });
            return;
        }

        setLoading(true);
        try {
            let imageUrl = formData.imageUrl;

            // Upload new image if selected
            if (selectedFile) {
                const fileName = `informationCard/${id}_${Date.now()}_${selectedFile.name}`;
                const uploadResult = await uploadFile(selectedFile, fileName);
                // uploadFile returns the download URL directly
                imageUrl = uploadResult;
                toast.success("Image uploaded successfully!", { autoClose: 2000 });
            }

            await updateDocument('informationCard', id, {
                Title: formData.Title,
                Description: formData.Description,
                imageUrl: imageUrl,
                // German Address Format
                streetAddress: formData.streetAddress,
                houseNumber: formData.houseNumber,
                postalCode: formData.postalCode,
                city: formData.city,
                state: formData.state,
                country: formData.country,
                // Event Details
                eventDate: formData.eventDate,
                eventTime: formData.eventTime,
                contact: formData.contact,
                // Computed fields for backward compatibility
                venue: `${formData.streetAddress} ${formData.houseNumber}, ${formData.postalCode} ${formData.city}, ${formData.state}, ${formData.country}`.trim().replace(/^,\s*|,\s*$/, ''),
                schedule: formData.eventDate && formData.eventTime ? `${formData.eventDate} um ${formData.eventTime}` : formData.eventDate || formData.eventTime || '',
            });
            sessionStorage.setItem('updateInformationCardSuccess', 'true');
            navigate("/informationcard");
        } catch (error) {
            toast.error("Error updating event: " + error.message, { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Header />
            <Sidebar
                id="menu-item4"
                id1="menu-items4"
                activeClassName="informationcard"
            />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header">
                        <div className="row">
                            <div className="col-sm-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/informationcard">Events Management</Link>
                                    </li>
                                    <li className="breadcrumb-item">
                                        <i className="feather-chevron-right">
                                            <FeatherIcon icon="chevron-right" />
                                        </i>
                                    </li>
                                    <li className="breadcrumb-item active">Edit Event</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <form onSubmit={handleSubmit}>
                                        <div className="row">
                                            <div className="col-12">
                                                <div className="form-heading">
                                                    <h4>Edit Event</h4>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Event Title <span className="login-danger">*</span></label>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <img 
                                                            src={calendar} 
                                                            alt="calendar" 
                                                            style={{ width: '24px', height: '24px', marginRight: '8px' }} 
                                                        />
                                                        <input
                                                            className="form-control"
                                                            type="text"
                                                            name="Title"
                                                            value={formData.Title}
                                                            onChange={handleChange}
                                                            placeholder="Enter event title"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Event Image</label>
                                                    <input
                                                        className="form-control"
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleFileChange}
                                                    />
                                                    
                                                    {/* Image Preview */}
                                                    {(imagePreview || currentImageUrl) && (
                                                        <div className="mt-3">
                                                            <div className="d-flex align-items-center justify-content-between mb-2">
                                                                <label className="form-label">Current Image:</label>
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    onClick={removeImage}
                                                                >
                                                                    Remove Image
                                                                </button>
                                                            </div>
                                                            <img
                                                                src={imagePreview || currentImageUrl}
                                                                alt="Event preview"
                                                                style={{
                                                                    width: '200px',
                                                                    height: '150px',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '8px',
                                                                    border: '1px solid #ddd'
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* German Address Fields */}
                                            <div className="col-12">
                                                <h5 style={{ marginTop: '20px', marginBottom: '15px', color: '#D4A853' }}>
                                                    Veranstaltungsort (Event Venue)
                                                </h5>
                                            </div>
                                            <div className="col-12 col-md-8 col-xl-8">
                                                <div className="form-group local-forms">
                                                    <label>Straße (Street) <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="streetAddress"
                                                        value={formData.streetAddress}
                                                        onChange={handleChange}
                                                        placeholder="z.B. Musterstraße"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4 col-xl-4">
                                                <div className="form-group local-forms">
                                                    <label>Hausnummer <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="houseNumber"
                                                        value={formData.houseNumber}
                                                        onChange={handleChange}
                                                        placeholder="z.B. 123a"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-4 col-xl-4">
                                                <div className="form-group local-forms">
                                                    <label>Postleitzahl (PLZ) <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="postalCode"
                                                        value={formData.postalCode}
                                                        onChange={handleChange}
                                                        placeholder="z.B. 12345"
                                                        pattern="[0-9]{5}"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-8 col-xl-8">
                                                <div className="form-group local-forms">
                                                    <label>Stadt (City) <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="city"
                                                        value={formData.city}
                                                        onChange={handleChange}
                                                        placeholder="z.B. Berlin"
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Bundesland (State)</label>
                                                    <select
                                                        className="form-control"
                                                        name="state"
                                                        value={formData.state}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Bundesland wählen</option>
                                                        <option value="Baden-Württemberg">Baden-Württemberg</option>
                                                        <option value="Bayern">Bayern</option>
                                                        <option value="Berlin">Berlin</option>
                                                        <option value="Brandenburg">Brandenburg</option>
                                                        <option value="Bremen">Bremen</option>
                                                        <option value="Hamburg">Hamburg</option>
                                                        <option value="Hessen">Hessen</option>
                                                        <option value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</option>
                                                        <option value="Niedersachsen">Niedersachsen</option>
                                                        <option value="Nordrhein-Westfalen">Nordrhein-Westfalen</option>
                                                        <option value="Rheinland-Pfalz">Rheinland-Pfalz</option>
                                                        <option value="Saarland">Saarland</option>
                                                        <option value="Sachsen">Sachsen</option>
                                                        <option value="Sachsen-Anhalt">Sachsen-Anhalt</option>
                                                        <option value="Schleswig-Holstein">Schleswig-Holstein</option>
                                                        <option value="Thüringen">Thüringen</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Land (Country)</label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="country"
                                                        value={formData.country}
                                                        onChange={handleChange}
                                                        placeholder="Deutschland"
                                                        readOnly
                                                    />
                                                </div>
                                            </div>
                                            
                                            {/* Event Time Fields */}
                                            <div className="col-12">
                                                <h5 style={{ marginTop: '20px', marginBottom: '15px', color: '#D4A853' }}>
                                                    Veranstaltungszeit (Event Time)
                                                </h5>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Datum (Date) <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="date"
                                                        name="eventDate"
                                                        value={formData.eventDate}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Uhrzeit (Time) <span className="login-danger">*</span></label>
                                                    <input
                                                        className="form-control"
                                                        type="time"
                                                        name="eventTime"
                                                        value={formData.eventTime}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-xl-6">
                                                <div className="form-group local-forms">
                                                    <label>Kontaktinformation (Contact)</label>
                                                    <input
                                                        className="form-control"
                                                        type="text"
                                                        name="contact"
                                                        value={formData.contact}
                                                        onChange={handleChange}
                                                        placeholder="E-Mail oder Telefon"
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-group local-forms">
                                                    <label>Event Description <span className="login-danger">*</span></label>
                                                    <TextEditor 
                                                        ref={editorRef} 
                                                        onChange={(data) => setFormData(prevData => ({ ...prevData, Description: data }))} 
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-12">
                                                <div className="form-action-group text-end">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary submit-form me-2"
                                                        disabled={loading}
                                                    >
                                                        {loading ? "Updating..." : "Update"}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary cancel-form"
                                                        onClick={() => navigate("/informationcard")}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                    <ToastContainer />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditInformationCard;
